import React, { useMemo } from 'react';
import { required, requiredNonZero } from '~/app/helpers/validation';
import Loader from '~/app/common/Loader';
import useForm, { UseFormOptionFields } from '~/app/modules/form/useForm';
import classes from './style.module.scss';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import Details from './Details';
import Footer from './Footer';
import { useStoreState } from '~/store';
import { IssueOrServiceType } from '~/app/data/statuses/chat';
import { WorkOrderTaskUserList, WorkOrderTimeEntryPayload } from '~/workorder/types';

export type FormProps = {
    onClose(): void;
    mode: 'add' | 'edit';
    workOrderId: number;
    taskId: number;
    type: IssueOrServiceType;
    initialEntry?: WorkOrderTaskUserList;
    onSaved: () => void;
};

export type FormComponent = React.FC<FormProps>;

const Form: FormComponent = ({ onClose, mode, workOrderId, taskId, type, initialEntry, onSaved }) => {
    const isHPCodeRequired = useStoreState(state => state.issue.isHPCodeRequired);

    const formFields = useMemo(() => {
        const fields: UseFormOptionFields = [
            ['comment', {}],
            ['ordinaryHours', { validators: [['required', required]] }],
            ['overTime50', {}],
            ['overTime100', {}],
            ['workType', { validators: [['required', requiredNonZero]] }],
        ];

        if (isHPCodeRequired) {
            fields.push(['hpCode', { validators: [['required', required]] }]);
        }

        return fields;
    }, [isHPCodeRequired]);

    const defaultValues = useMemo((): Partial<WorkOrderTimeEntryPayload> => ({
        comment: initialEntry?.comment ?? '',
        hpCode: initialEntry?.hpCode ?? 0,
        ordinaryHours: initialEntry?.ordinaryHours ?? undefined,
        overTime50: initialEntry?.overTime50 ?? undefined,
        overTime100: initialEntry?.overTime100 ?? undefined,
        workType: initialEntry?.workType ?? (type === IssueOrServiceType.SERVICE ? 3 : 0),
    }), [initialEntry, type]);

    const { values, removeForm } = useForm<WorkOrderTimeEntryPayload>({
        formName: 'workOrderTimeEntry',
        fields: formFields,
        defaultValues,
    });

    useDidMountEffect(() => (() => removeForm()));

    if (!values) {
        return <Loader size="32" />;
    }

    return (
        <>
            <div className={classes['container']}>
                <Details />
            </div>
            <Footer
                onClose={onClose}
                mode={mode}
                workOrderId={workOrderId}
                taskId={taskId}
                type={type}
                entryId={initialEntry?.id}
                onSaved={onSaved}
            />
        </>
    );
};

export default Form;
